import { OnDragEndResponder, OnDragStartResponder } from '@hello-pangea/dnd';
import { PencilIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { twJoin } from 'tailwind-merge';

import IconButton from '@/components/ui/custom/buttons/IconButton';
import MainButton from '@/components/ui/custom/buttons/MainButton';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import Text from '@/components/ui/custom/dataDisplay/Text';
import { Tooltip } from '@/components/ui/custom/dataDisplay/Tooltip';
import {
  DragDropItem,
  DragDropList,
} from '@/components/ui/custom/dataInput/DragDrop';
import SpanInput from '@/components/ui/custom/dataInput/SpanInput';
import ModalDisable from '@/components/ui/custom/modals/ModalDisable';
import { ModularCard } from '@/components/ui/custom/modular/ModularCard';
import { useScrollIntoView } from '@/data/hook/useScrollIntoView';
import useToggle from '@/data/hook/useToggle';
import { deleteChapter, updateChapter } from '@/data/services/chapterServices';
import { bookType } from '@/models/Book';
import Chapter from '@/models/Chapter';
import Subchapter from '@/models/Subchapter';
import { dropHandler } from '@/utils/dragdrop';
import { getErrorMessage } from '@/utils/getErrorMessage';
import { KeyboardKeys } from '@/utils/Keyboard';
import alert from '@/utils/shared/alert';
import { useMutation } from '@tanstack/react-query';
import { debounce } from 'lodash';
import SubchapterItem from '../subchapters/Subchapter';

export type SubchaptersProps = {
  chapter: Chapter;
  selectedChapter: boolean;
  activeSubchapterId?: number;
  onChapterOrSubchapter: (selected: Chapter | Subchapter) => void;
  readOnly?: boolean;
  onAddSubchapter?: (chapter: Chapter) => Promise<Subchapter>;
  isAddingSubchapter?: boolean;
  onReorder?: (chapterId: number, subchapters: Subchapter[]) => void;
  onUpdateChapter?: (chapter: Chapter) => void;
  lessonId: number;
  bookId: number;
  bookType: bookType;
  onDeleteChapter?: (chapter: Chapter) => void;
  isUniqueChapter?: boolean;
  startOpen?: boolean;
  onScrollChapters?: () => void;
};

export default function Subchapters(props: SubchaptersProps) {
  const [t] = useTranslation('translation', {
    keyPrefix: 'common',
  });

  const { t: tChapter } = useTranslation('translation', {
    keyPrefix: 'chapter',
  });

  const {
    chapter,
    onChapterOrSubchapter,
    readOnly,
    onAddSubchapter,
    isAddingSubchapter,
    selectedChapter,
    onReorder,
    onUpdateChapter,
    lessonId,
    bookId,
    bookType,
    onDeleteChapter,
    isUniqueChapter,
  } = props;

  const { items = [] } = chapter;

  const subchapters = items;

  const [editing, setEditing] = useState(false);

  const { isOpen, toggle, open } = useToggle();

  const [isDeleting, setIsDeleting] = useState(false);

  const handleDragEnd: OnDragEndResponder = res => {
    const reordenedResult = dropHandler(res, subchapters);
    onReorder?.(chapter.id, reordenedResult);
  };

  const handleDragStart: OnDragStartResponder = res => {
    const subchapter = subchapters.at(res.source.index);
    if (!subchapter) return;
    onChapterOrSubchapter?.(subchapter);
  };

  const handleEdit = () => {
    setEditing(prev => !prev);
    onChapterOrSubchapter?.(chapter);
  };

  const [chapterTitle, setChapterTitle] = useState(chapter.title);

  const [changed, setChanged] = useState(false);

  const [errorLabel, setErrorLabel] = useState<string>();

  const { mutate: changeChapterTitle } = useMutation(updateChapter, {
    onSuccess: data => {
      onUpdateChapter?.(data);
      alert.success(tChapter('successUpdated'));
    },
    onError: err => {
      const errMessage = getErrorMessage(err);
      alert.error(errMessage);
    },
  });

  const { mutate: removeChapter, isLoading: isRemoveChapter } = useMutation(
    deleteChapter,
    {
      onSuccess: () => {
        onDeleteChapter?.(chapter);
        setIsDeleting(false);
        alert.success(tChapter('successDeleting'));
      },
      onError: err => {
        const errMessage = getErrorMessage(err);
        alert.error(errMessage);
      },
    },
  );

  const updateChapterTitle = useCallback(
    (title: string | null) => {
      if (!title) {
        setChanged(false);
        return;
      }

      changeChapterTitle({
        params: { lessonId, bookId, bookType, chapterId: chapter.id },
        body: { title },
      });
      setChanged(false);
    },
    [changeChapterTitle, lessonId, bookId, bookType, chapter.id],
  );

  const debouncedUpdate = useMemo(
    () => debounce(updateChapterTitle, 500),
    [updateChapterTitle],
  );

  useEffect(() => {
    if (changed) debouncedUpdate(chapterTitle);

    return () => {
      debouncedUpdate.cancel();
    };
  }, [debouncedUpdate, chapterTitle, changed]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const title = e.target.value;

    if (!title) {
      setErrorLabel(
        t('form.required', {
          field: tChapter('subchapter.title').toLowerCase(),
        }),
      );
    } else {
      setErrorLabel(undefined);
    }

    setChapterTitle(title);
    setChanged(true);
  };

  const addSubchapter = async () => {
    const newSubchapter = await onAddSubchapter?.(chapter);
    if (newSubchapter) onChapterOrSubchapter?.(newSubchapter);
  };

  useEffect(() => {
    if (selectedChapter) {
      open();
    }
  }, [selectedChapter, open]);

  const itemRef = useRef<HTMLLIElement>(null);

  useScrollIntoView(itemRef, {
    condition: !!selectedChapter,
    delay: 150,
  });

  return (
    <li
      ref={itemRef}
      className={twJoin(
        'flex flex-col gap-2.5 border-l border-secondary-content rounded-t-md',
        isOpen ? '' : 'rounded-b-md',
      )}
    >
      <ModalDisable
        isRequesting={isRemoveChapter}
        visible={isDeleting}
        onClickCancel={() => setIsDeleting(false)}
        translationString="deleteChapter"
        onClickConfirm={() => {
          removeChapter({
            lessonId,
            bookId,
            bookType,
            chapterId: chapter.id,
          });
        }}
      />
      <ModularCard.Header
        onOpen={() => {
          onChapterOrSubchapter?.(chapter);
          toggle();
        }}
        title={chapter.title}
        isOpen={isOpen}
        className={{
          base: twJoin(
            'p-0 py-1 px-2.5',
            selectedChapter ? 'bg-secondary-content rounded-md gap-0' : '',
          ),
          figure: 'bg-transparent',
          section: 'font-500 w-full max-w-[70%]',
          button: 'right-0.5',
        }}
        titleFallback={
          <SpanInput
            errorLabelText={errorLabel}
            value={chapterTitle}
            isEditing={editing && selectedChapter && !readOnly}
            onChange={handleChange}
            errorLabelPosition="bottom"
            className={{ input: 'w-36' }}
            onKeyDown={event => {
              if (event.key === KeyboardKeys.Enter) {
                setEditing(false);
              }
            }}
          >
            {value => <Text text={value} className="truncate" />}
          </SpanInput>
        }
      >
        <ConditionalRenderer condition={!readOnly}>
          <aside className="flex gap-2">
            <Tooltip text={t('edit')}>
              <IconButton
                testId="editButton"
                icon={
                  <PencilIcon className="w-4 text-primary transition-all duration-150 ease-in-out hover:text-primary/50" />
                }
                onClick={e => {
                  e.stopPropagation();
                  handleEdit();
                }}
              />
            </Tooltip>

            <Tooltip text={t('remove')}>
              <IconButton
                loading={isRemoveChapter}
                testId="removeButton"
                icon={<TrashIcon />}
                className="w-4 text-error transition-all duration-150 ease-in-out hover:text-error/50"
                onClick={e => {
                  e.stopPropagation();
                  if (isUniqueChapter) {
                    alert.warning(tChapter('notDeleteUniqueChapter'));
                    return;
                  }
                  setIsDeleting(true);
                }}
              />
            </Tooltip>
          </aside>
        </ConditionalRenderer>
      </ModularCard.Header>
      <ModularCard.Accordion isOpen={isOpen} className={{ base: 'pl-1.5' }}>
        <DragDropList
          groupId="subchapters"
          onDragEnd={handleDragEnd}
          onDragStart={handleDragStart}
          className="relative overflow-hidden"
        >
          {items.map((subchapter, index) => (
            <DragDropItem
              id={'subchapter' + subchapter.id}
              index={index}
              hideDragIconHandleOnDisabled
              key={'subchapter' + subchapter.id}
              disabled={readOnly}
              className={{ button: 'top-0' }}
            >
              <SubchapterItem
                key={subchapter.id}
                {...props}
                subchapter={subchapter}
              />
            </DragDropItem>
          ))}
        </DragDropList>
      </ModularCard.Accordion>
      <ConditionalRenderer condition={!readOnly && selectedChapter}>
        <MainButton
          color="custom"
          className="relative text-primary font-600 gap-1 pl-2 pb-2.5"
          text={t('add')}
          icon={<PlusIcon />}
          onClick={addSubchapter}
          loading={isAddingSubchapter}
        />
      </ConditionalRenderer>
    </li>
  );
}
